<template>
  <div id="closet-guide">
    <div class="page-header">
      <div class="title">사이즈 가이드</div>
      <div class="subtitle">구매 전, 이 사이즈가 나에게 맞을지 내 옷 기록으로 미리 확인하세요</div>
    </div>

    <div class="category-tabs">
      <button
        v-for="opt in categoryOptions"
        :key="opt.value"
        class="tab-btn"
        :class="{ active: category === opt.value }"
        @click="category = opt.value"
      >{{ opt.label }}</button>
    </div>

    <div class="guide-body">
      <!-- 좌: 핏 체크 -->
      <div class="fitcheck-card">
        <div class="fc-head">
          <div class="fc-title"><span class="fc-ic">🔎</span> 핏 체크</div>
          <span class="fc-unit-note">cm 기준</span>
        </div>
        <p class="fc-desc">
          사려는 옷의 <b>상품 사이즈표 실측</b>을 입력하면, 내가 <b>딱 맞다</b>고 평가한 옷들과 비교해 부위별로 판정해 드려요.
        </p>

        <div class="fc-sub" v-if="lengthKeys.length">
          <label>소분류 <span class="fc-sub-hint">(총장·소매 등 길이 판정용)</span></label>
          <select v-model="checkSub">
            <option value="">선택 안 함</option>
            <option v-for="s in subOptions" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <div class="fc-fields">
          <div class="fc-field" v-for="f in fields" :key="f.key">
            <label>
              {{ f.label }}
              <span class="fc-target" v-if="recoMap[f.key] && recoMap[f.key].recommended != null">
                내 추천 {{ recoMap[f.key].recommended }}{{ f.unit }}
              </span>
            </label>
            <div class="fc-input-row">
              <input type="number" step="0.1" v-model.number="input[f.key]" :placeholder="f.unit" />
              <span class="fc-verdict" :class="verdicts[f.key].cls">{{ verdicts[f.key].text }}</span>
            </div>
          </div>
        </div>

        <div class="fc-overall" :class="overall.cls" v-if="anyInput">
          <span class="ov-ic">{{ overall.icon }}</span>
          <div class="ov-text">
            <div class="ov-title">{{ overall.title }}</div>
            <div class="ov-desc">{{ overall.desc }}</div>
          </div>
        </div>
        <div class="fc-hint" v-else>
          위 칸에 상품 실측을 입력하면 실시간으로 판정됩니다.
        </div>
      </div>

      <!-- 우: 내 추천 사이즈 -->
      <aside class="reco-card">
        <div class="reco-head">
          <div class="reco-title"><span class="reco-ic">📏</span> 내 추천 사이즈</div>
          <button class="unit-toggle" @click="toggleUnit">
            <span :class="{ on: !toInch }">cm</span><span class="sep">/</span><span :class="{ on: toInch }">in</span>
          </button>
        </div>
        <div class="reco-cat">{{ getCategoryLabel(category) }}</div>

        <template v-if="hasReco">
          <div class="reco-rows" v-if="fitReco.common.length">
            <div class="reco-sec">공통 부위</div>
            <div class="reco-row" v-for="row in fitReco.common" :key="row.key" :class="{ empty: row.recommended == null }">
              <span class="rr-part">{{ row.label }}</span>
              <span class="rr-val" v-if="row.recommended != null">{{ conv(row.recommended, row.unit) }}<em>{{ unit(row.unit) }}</em></span>
              <span class="rr-val muted" v-else>–</span>
              <span class="rr-basis">{{ basis(row) }}</span>
            </div>
          </div>
          <div class="reco-rows" v-for="g in fitReco.lengthGroups" :key="g.key">
            <div class="reco-sec">{{ g.label }} <span class="sub">소분류별</span></div>
            <div class="reco-row" v-for="it in g.items" :key="it.subCategory">
              <span class="rr-part">{{ it.subCategory }}</span>
              <span class="rr-val">{{ conv(it.row.recommended, g.unit) }}<em>{{ unit(g.unit) }}</em></span>
              <span class="rr-basis">{{ basis(it.row) }}</span>
            </div>
          </div>
        </template>
        <div class="reco-empty" v-else>
          <p>아직 <b>핏 평가</b>가 있는 {{ getCategoryLabel(category) }} 옷이 없어요.</p>
          <p class="hint">옷을 등록할 때 실측과 핏(작음/딱맞음/큼)을 남기면 이 표가 채워집니다.</p>
          <button class="reco-reg" @click="goRegister">＋ 의류 등록</button>
        </div>
        <div class="reco-foot" v-if="hasReco">‘딱 맞음’으로 평가한 옷들의 실측 범위를 기준으로 추정한 값이에요.</div>
      </aside>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  CLOTHING_CATEGORY_OPTIONS,
  MEASUREMENT_FIELDS,
  SUB_CATEGORY_SUGGESTIONS,
  computeFitReco,
  getCategoryLabel,
  convertMeasure,
  displayUnit,
  UNIT_STORAGE_KEY,
  type FitRecoRow,
  type FitRecoResult,
} from "../class";
import * as closetApi from "../api";

const LENGTH_KEYS: Record<string, string[]> = {
  TOP: ["total_length", "sleeve_length"],
  OUTER: ["total_length", "sleeve_length"],
  ONEPIECE: ["total_length", "sleeve_length"],
  BOTTOM: ["total_length"],
  SHOES: [],
};

export default defineComponent({
  name: "ClosetGuideView",
  setup() {
    const router = useRouter();
    const allClothing = ref<any[]>([]);
    const category = ref("TOP");
    const checkSub = ref("");
    const input = reactive<Record<string, number | null>>({});

    const toInch = ref<boolean>(localStorage.getItem(UNIT_STORAGE_KEY) === "in");
    const toggleUnit = () => {
      toInch.value = !toInch.value;
      localStorage.setItem(UNIT_STORAGE_KEY, toInch.value ? "in" : "cm");
    };

    const fields = computed(() => MEASUREMENT_FIELDS[category.value] || []);
    const lengthKeys = computed(() => LENGTH_KEYS[category.value] || []);
    const subOptions = computed(() => SUB_CATEGORY_SUGGESTIONS[category.value] || []);

    const fitReco = computed<FitRecoResult>(() =>
      computeFitReco(category.value, allClothing.value)
    );
    const hasReco = computed(
      () =>
        fitReco.value.common.some((r) => r.recommended != null) ||
        fitReco.value.lengthGroups.length > 0
    );

    // 부위별 추천 행 맵 (공통 + 선택 소분류의 길이)
    const recoMap = computed<Record<string, FitRecoRow>>(() => {
      const map: Record<string, FitRecoRow> = {};
      fitReco.value.common.forEach((r) => (map[r.key] = r));
      fitReco.value.lengthGroups.forEach((g) => {
        const it = g.items.find((i) => i.subCategory === checkSub.value);
        if (it) map[g.key] = it.row;
      });
      return map;
    });

    const judge = (key: string) => {
      const v = Number(input[key]);
      if (!v) return { cls: "none", text: "" };
      const row = recoMap.value[key];
      if (!row || row.recommended == null) return { cls: "nodata", text: "데이터 없음" };
      const lo = row.low;
      const hi = row.high;
      if (row.boundType === "range" && lo != null && hi != null) {
        if (v < lo) return { cls: "tight", text: "작을 수 있음" };
        if (v > hi) return { cls: "loose", text: "클 수 있음" };
        return { cls: "ok", text: "적정" };
      }
      if (row.boundType === "atLeast" && lo != null) {
        return v < lo ? { cls: "tight", text: "작을 수 있음" } : { cls: "ok", text: "적정" };
      }
      if (row.boundType === "atMost" && hi != null) {
        return v > hi ? { cls: "loose", text: "클 수 있음" } : { cls: "ok", text: "적정" };
      }
      const rec = row.recommended;
      const tol = Math.max(1, rec * 0.03);
      if (v < rec - tol) return { cls: "tight", text: "작을 수 있음" };
      if (v > rec + tol) return { cls: "loose", text: "클 수 있음" };
      return { cls: "ok", text: "적정" };
    };

    const verdicts = computed<Record<string, { cls: string; text: string }>>(() => {
      const out: Record<string, { cls: string; text: string }> = {};
      fields.value.forEach((f) => (out[f.key] = judge(f.key)));
      return out;
    });

    const anyInput = computed(() =>
      fields.value.some((f) => Number(input[f.key]) > 0)
    );

    const overall = computed(() => {
      const vs = fields.value
        .map((f) => verdicts.value[f.key].cls)
        .filter((c) => c === "tight" || c === "ok" || c === "loose");
      if (vs.length === 0)
        return { cls: "gray", icon: "📝", title: "판정할 데이터가 부족해요", desc: "핏 평가가 있는 내 옷과 비교합니다. 실측을 더 입력해 보세요." };
      const tight = vs.filter((c) => c === "tight").length;
      const loose = vs.filter((c) => c === "loose").length;
      if (tight === 0 && loose === 0)
        return { cls: "ok", icon: "👍", title: "잘 맞을 거예요", desc: "모든 부위가 내 적정 범위 안에 들어와요." };
      if (tight > 0 && loose === 0)
        return { cls: "tight", icon: "↑", title: "작을 수 있어요", desc: "한 치수 큰 사이즈를 함께 보는 걸 권장해요." };
      if (loose > 0 && tight === 0)
        return { cls: "loose", icon: "↓", title: "클 수 있어요", desc: "한 치수 작은 사이즈를 함께 보는 걸 권장해요." };
      return { cls: "mixed", icon: "≈", title: "부위별로 달라요", desc: "부위마다 핏이 갈려요. 중요하게 보는 부위 기준으로 선택하세요." };
    });

    // 추천 표 표시 헬퍼
    const conv = (v: number, u: string) => convertMeasure(v, u, toInch.value);
    const unit = (u: string) => displayUnit(u, toInch.value);
    const basis = (row: FitRecoRow) => {
      const u = displayUnit(row.unit, toInch.value);
      const cv = (v: number | null) => (v == null ? "" : convertMeasure(v, row.unit, toInch.value));
      let range = "";
      if (row.boundType === "range" && row.low != null && row.high != null) range = `${cv(row.low)}~${cv(row.high)}${u} · `;
      else if (row.boundType === "atLeast" && row.low != null) range = `${cv(row.low)}${u} 이상 · `;
      else if (row.boundType === "atMost" && row.high != null) range = `${cv(row.high)}${u} 이하 · `;
      return range + row.basis;
    };

    const goRegister = () => {
      router.push({ name: "closet-clothing-register", query: { category: category.value } });
    };

    onMounted(async () => {
      const data = await closetApi.getClothingList(null);
      allClothing.value = Array.isArray(data) ? data : [];
    });

    return {
      categoryOptions: CLOTHING_CATEGORY_OPTIONS,
      category,
      checkSub,
      input,
      fields,
      lengthKeys,
      subOptions,
      fitReco,
      hasReco,
      recoMap,
      verdicts,
      anyInput,
      overall,
      toInch,
      toggleUnit,
      conv,
      unit,
      basis,
      getCategoryLabel,
      goRegister,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/closet-guide.scss";
</style>
